import { ChangeEvent, useEffect, useRef, useState } from "react";
import { Button, styled, useMediaQuery, useTheme } from "@mui/material";

type DocUploadProps = {
  onUpload?(): void;
  title?: string;
  setFile?(file: File | undefined): void;
  style?: any;
  isFileDeleted?: boolean;
  acceptImages?: boolean;
  disabled?: boolean;
  file?: File | undefined;
  uploadButton?: React.ReactNode;
};

const DocUpload = ({
  uploadButton,
  file,
  title,
  setFile,
  style,
  isFileDeleted,
  acceptImages,
  disabled,
}: DocUploadProps) => {
  const MAX_FILE_SIZE = 50000000;
  const [selectedFile, setSelectedFile] = useState<File | undefined>(file);
  const [error, setError] = useState("");
  const uploadRef = useRef<HTMLInputElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (!selectedFile) {
      return;
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    // free memory when ever this component is unmounted
    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile]);

  useEffect(() => {
    if (isFileDeleted && selectedFile) {
      handleClearClick();
    }
  }, [isFileDeleted]);

  const handleButtonClick = () => {
    uploadRef.current?.click();
  };

  const handleClearClick = () => {
    if (uploadRef.current) {
      // @ts-ignore
      uploadRef.current.value = null;
      if (setFile) {
        setFile(undefined);
      }
      setSelectedFile(undefined);
    }
  };

  const handleUpload = (e: ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.files?.length) {
      const { size } = e.target.files[0];
      if (size > MAX_FILE_SIZE) {
        setError(`Selected file is too big! Size should not exceed ${MAX_FILE_SIZE / 1000000} MB`);
      } else {
        if (setFile) {
          setFile(e.target.files[0]);
        }
        setSelectedFile(e.target.files[0]);
        setError("");
      }
    }
  };

  const acceptedFiles = ["application/pdf"];
  let defaultTitle = "Max 50 MB in ";

  if (acceptImages) {
    acceptedFiles.push("image/png, image/gif, image/jpeg");
    defaultTitle = "Max 50 MB in .jpg/.jpeg/.png/.gif/.pdf format";
  } else {
    defaultTitle += ".pdf format";
  }

  return (
    <Wrapper
      style={style}
      sx={{
        p: isMobile ? '12px 10px' : 12,
        backgroundColor: ({ palette }) => palette.background.paper,
        borderColor: ({ palette }) => palette.action.disabled,
      }}
    >
      <ContentContainer isMobile={isMobile}>
        <FileInfoContainer isMobile={isMobile}>
          <p>{error ? <Error>{error}</Error> : selectedFile ? selectedFile.name : title ? title : defaultTitle}</p>
        </FileInfoContainer>
        <input accept={acceptedFiles.join(", ")} ref={uploadRef} type="file" hidden onChange={handleUpload} />
        {!selectedFile && (
          <ButtonContainer isMobile={isMobile}>
            <Button
              disabled={disabled}
              onClick={() => handleButtonClick()}
              fullWidth={isMobile}
              size={isMobile ? "small" : "medium"}
              sx={{
                position: "absolute",
                right: '12px',
                top: isMobile ? '8px' : '12px',
                textTransform: "capitalize",
                "&:hover": {
                  border: "none"
                }
              }}
            >
              Choose File
            </Button>
          </ButtonContainer>
        )}
        {selectedFile && (
          <BtnGroup isMobile={isMobile}>
            <Button
              disabled={disabled}
              variant="contained"
              onClick={() => handleClearClick()}
              fullWidth={isMobile}
              size={isMobile ? "small" : "medium"}
              disableElevation
              sx={{
                textTransform: 'none',
                backgroundColor: 'black',
                color: 'white',
                border: 'none',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                  color: 'black',
                  border: 'none',
                  boxShadow: 'none',
                },
              }}
            >
              Clear
            </Button>
            {uploadButton && uploadButton}
          </BtnGroup>
        )}
      </ContentContainer>
    </Wrapper>
  );
};

const ContentContainer = styled('div') <{ isMobile?: boolean }>`
  display: flex;
  width: 100%;
  flex-direction: ${props => props.isMobile ? 'column' : 'row'};
  justify-content: space-between;
  align-items: ${props => props.isMobile ? 'center' : 'center'};
`;

const FileInfoContainer = styled('div') <{ isMobile?: boolean }>`
  overflow: hidden;
  text-overflow: ellipsis;
  width: ${props => props.isMobile ? '100%' : 'auto'};
  margin-bottom: ${props => props.isMobile ? '10px' : '0'};
  padding: ${props => props.isMobile ? '0 5px' : '0'};
  
  p {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: ${props => props.isMobile ? 'normal' : 'nowrap'};
    font-size: ${props => props.isMobile ? '14px' : 'inherit'};
    text-align: ${props => props.isMobile ? 'start' : 'left'};
    word-break: break-word;
  }
`;

const ButtonContainer = styled('div') <{ isMobile?: boolean }>`
  width: ${props => props.isMobile ? '100%' : 'auto'};
  flex-direction: ${props => props.isMobile ? 'column' : 'row'};
`;

const BtnGroup = styled("div") <{ isMobile?: boolean }>`
  display: flex;
  align-items: center;
  flex-direction: ${props => props.isMobile ? 'row-reverse' : 'row'};
  gap: 12px;
  width: ${props => props.isMobile ? '100%' : 'auto'};
  
  button {
    margin-bottom: ${props => props.isMobile ? '8px' : '0'};
    width: ${props => props.isMobile ? '100%' : 'auto'};
  }
`;

const Wrapper = styled("div")`
  border-radius: 16px;
  border: 2px dashed;
  margin: 12px auto;
  width: 100%;
  display: flex;
  position: relative;

  p {
    margin: 0;
    color: #3d3d3d;
  }

  button {
    padding: 6px 16px;
    max-width: max-content;
  }
`;

const Error = styled("div")`
  color: red;
`;

export default DocUpload;
